import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  MoodleService,
  MoodleCourse,
  MoodleSyncJob,
  MoodleBulkSyncJob,
} from '../../../services/moodle.service';
import { BucketService } from '../../../services/bucket.service';
import { OrganisationService } from '../../../services/organisation.service';

@Component({
  selector: 'app-moodle-import-dialog',
  templateUrl: './moodle-import-dialog.component.html',
  styleUrl: './moodle-import-dialog.component.scss',
})
export class MoodleImportDialogComponent implements OnInit, OnDestroy {
  @Input() bucketId!: string;

  courses: MoodleCourse[] = [];
  selectedCourses = new Set<string>();
  selectedCourseByKey = new Map<string, MoodleCourse>();
  courseTotal = 0;
  coursePage = 0;
  coursePageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];
  searchInput = '';
  search = '';
  loading = true;
  syncing = false;
  syncFrequency = 'NEVER';
  syncJob: MoodleSyncJob | null = null;
  bulkSyncJob: MoodleBulkSyncJob | null = null;

  private syncSub?: Subscription;
  private bulkSyncSub?: Subscription;

  constructor(
    protected dialogRef: NbDialogRef<MoodleImportDialogComponent>,
    protected moodleService: MoodleService,
    protected bucketService: BucketService,
    protected organisationService: OrganisationService,
    protected nbToastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit() {
    this.loadCourses();

    this.syncSub = this.moodleService.activeSyncJob$.subscribe((job) => {
      this.syncJob = job;
      if (job?.status === 'COMPLETED') {
        this.syncing = false;
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleImport.syncSuccess'),
          this.t.instant('toasts.success')
        );
        this.bucketService.getBucketEntries(this.bucketId).subscribe();
        this.organisationService.getOrganisation().subscribe();
      }
      if (job?.status === 'FAILED') {
        this.syncing = false;
        this.nbToastrService.danger(
          job.error_message ||
            this.t.instant('dialogs.moodleImport.syncFailed'),
          this.t.instant('toasts.error')
        );
      }
    });

    this.bulkSyncSub = this.moodleService.activeBulkSyncJob$.subscribe((job) => {
      this.bulkSyncJob = job;
      if (job?.status === 'COMPLETED') {
        this.syncing = false;
        const msg = job.failed_courses > 0
          ? this.t.instant('dialogs.moodleImport.bulkSyncPartial', {
              completed: job.completed_courses,
              failed: job.failed_courses,
            })
          : this.t.instant('dialogs.moodleImport.bulkSyncSuccess', {
              completed: job.completed_courses,
            });
        this.nbToastrService.success(msg, this.t.instant('toasts.success'));
        this.bucketService.getBucketEntries(this.bucketId).subscribe();
        this.organisationService.getOrganisation().subscribe();
      }
      if (job?.status === 'FAILED') {
        this.syncing = false;
        this.nbToastrService.danger(
          job.error_message ||
            this.t.instant('dialogs.moodleImport.bulkSyncFailed'),
          this.t.instant('toasts.error')
        );
      }
    });
  }

  ngOnDestroy() {
    this.syncSub?.unsubscribe();
    this.bulkSyncSub?.unsubscribe();
    this.moodleService.stopPolling();
    this.moodleService.stopBulkPolling();
  }

  loadCourses() {
    this.loading = true;
    const search = this.search.trim();
    this.moodleService.listMoodleCoursesPage({
      limit: this.coursePageSize,
      offset: this.coursePage * this.coursePageSize,
      ...(search ? { search } : {}),
    }).subscribe({
      next: (res) => {
        this.courses = res.data;
        this.courseTotal = res.total ?? res.pagination?.total ?? this.courses.length;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.nbToastrService.danger(
          this.moodleService.translateMoodleError(
            err,
            'dialogs.moodleImport.loadCoursesFailed'
          ),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  applyCourseSearch(): void {
    const nextSearch = this.searchInput.trim();
    if (nextSearch === this.search) return;
    this.search = nextSearch;
    this.coursePage = 0;
    this.loadCourses();
  }

  clearCourseSearch(): void {
    this.searchInput = '';
    if (!this.search) return;
    this.search = '';
    this.coursePage = 0;
    this.loadCourses();
  }

  courseKey(course: MoodleCourse): string {
    return course.canonical_id || `${course.registration_id || 'default'}:${course.id}`;
  }

  isCourseDisabled(course: MoodleCourse): boolean {
    return course.registration_selectable === false;
  }

  getCourseDisabledHint(course: MoodleCourse): string {
    const site = course.site_name || course.site_url || this.t.instant('dialogs.moodleImport.thisMoodleSite');
    return this.t.instant('dialogs.moodleImport.courseDisabledHint', { site });
  }

  toggleCourse(course: MoodleCourse) {
    if (this.isCourseDisabled(course)) return;
    const key = this.courseKey(course);
    if (this.selectedCourses.has(key)) {
      this.selectedCourses.delete(key);
      this.selectedCourseByKey.delete(key);
    } else {
      this.selectedCourses.add(key);
      this.selectedCourseByKey.set(key, course);
    }
  }

  isCourseSelected(course: MoodleCourse): boolean {
    return this.selectedCourses.has(this.courseKey(course));
  }

  toggleAll() {
    if (this.allSelected) {
      this.selectableCourses.forEach((c) => {
        const key = this.courseKey(c);
        this.selectedCourses.delete(key);
        this.selectedCourseByKey.delete(key);
      });
    } else {
      this.selectableCourses.forEach((c) => {
        const key = this.courseKey(c);
        this.selectedCourses.add(key);
        this.selectedCourseByKey.set(key, c);
      });
    }
  }

  get selectableCourses(): MoodleCourse[] {
    return this.courses.filter((c) => !this.isCourseDisabled(c));
  }

  get allSelected(): boolean {
    return this.selectableCourses.length > 0 && this.selectableCourses.every((c) => this.selectedCourses.has(this.courseKey(c)));
  }

  onCoursePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.courseTotal / this.coursePageSize) - 1, 0);
    this.coursePage = Math.max(0, Math.min(page, maxPage));
    this.loadCourses();
  }

  onCoursePageSizeChange(size: number): void {
    this.coursePageSize = Math.min(Math.max(size, 1), 100);
    this.coursePage = 0;
    this.loadCourses();
  }

  startSync() {
    if (this.selectedCourses.size === 0) return;

    this.syncing = true;
    const selected = Array.from(this.selectedCourseByKey.values()).filter(
      (c) => !this.isCourseDisabled(c)
    );

    if (selected.length === 1) {
      // Single course — use existing single sync
      const course = selected[0];
      this.moodleService
        .startSync(
          course.id,
          course.fullname,
          this.bucketId,
          this.syncFrequency,
          course.registration_id,
          course.canonical_id
        )
        .subscribe({
          error: (err) => {
            this.syncing = false;
            this.nbToastrService.danger(
              this.moodleService.translateMoodleError(
                err,
                'dialogs.moodleImport.syncStartFailed'
              ),
              this.t.instant('toasts.error')
            );
          },
        });
    } else {
      // Multiple courses — use bulk sync
      const courseList = selected.map((c) => ({
        moodle_course_id: c.id,
        course_name: c.fullname,
        registration_id: c.registration_id,
        moodle_course_uuid: c.canonical_id,
      }));
      this.moodleService.startBulkSync(courseList, this.bucketId, this.syncFrequency).subscribe({
        error: (err) => {
          this.syncing = false;
          this.nbToastrService.danger(
            this.moodleService.translateMoodleError(
              err,
              'dialogs.moodleImport.bulkSyncStartFailed'
            ),
            this.t.instant('toasts.error')
          );
        },
      });
    }
  }

  close() {
    this.dialogRef.close();
  }

  get progressPercent(): number {
    if (this.bulkSyncJob) {
      return this.bulkSyncJob.progress || 0;
    }
    if (!this.syncJob || this.syncJob.total_items === 0) return 0;
    return Math.round(
      (this.syncJob.processed_items / this.syncJob.total_items) * 100
    );
  }

  get currentSyncLabel(): string {
    if (this.bulkSyncJob?.current_course_name) {
      return this.bulkSyncJob.current_course_name;
    }
    return '';
  }

  get isBulkSync(): boolean {
    return this.bulkSyncJob !== null && this.selectedCourses.size > 1;
  }
}
